/* @flow */
/* eslint-env es6,browser */

import * as u from './utils';
import adconfig from './adconfig';

/**
 * @description Implements adrotation functionality
 * @exports adrotation
 * @requires {@link module:utils}
 * @requires {@link module:adconfig}
 */
class _adrotation {
  _pageStart: number;
  _tidRotate: number;
  _getAdsCB: (string, Array<string>, boolean, boolean) => mixed;
  _getAdsCBCxt: any;
  _config: {
    excludeList: Array<string>,
    positions: Array<string>,
    delay: number,
    durationEnd: number
  };
  _rotationStatus: boolean;
  constructor() {
    /**
     * @global
     * @typedef {Object} rotationConfig
     * @property {Array<string>} [excludeList]
     * @property {Array<string>} positions
     * @property {number} delay
     * @property {number} durationEnd
     */
    this._config = {
      excludeList: [],
      positions: [],
      delay: 0,
      durationEnd: -1
    };
    this._pageStart = 0;
    this._tidRotate = 0;
    this._getAdsCB = u.noop;
    this._getAdsCBCxt = null;
    this._rotationStatus = false;
  }

  /**
   * updates config
   * @param {rotationConfig}
   */
  updateConfig(o: rotationConfig) {
    this._config.delay = o.duration;
    this._config.durationEnd = o.durationEnd || -1;
    this._config.positions = (o.positions || '').split(',');
    this._config.excludeList = u.getBrowserExcludeList(o.exclude);
  }

  /**
   * sets rotation status - whether current ad load is for page load or adrotation
   * @param {boolean} s
   */
  setRotationStatus(s: boolean) {
    this._rotationStatus = s;
  }

  /**
   * getter for rotation status - whether current ad load is for page load or adrotation
   * @return {boolean}
   */
  getRotationStatus(): boolean {
    return this._rotationStatus;
  }

  /**
   * initialize page start
   */
  setPageStart() {
    this._pageStart = new Date().getTime();
  }

  /**
   * @global
   * @typedef function
   * @callback getAdsCB
   * @param {string} page
   * @param {Array<string>} posList
   * @param {boolean} [bCache=true]
   * @param {boolean} [bRotate=false]
   */
  /**
   * setter for getAds callback used for ad rotation
   * @param {getAdsCB} cb - callback function
   * @param {Object} cxt - context
   */
  setGetAdsCB(cb: (string, Array<string>, boolean, boolean) => mixed, cxt: any) {
    this._getAdsCB = cb;
    this._getAdsCBCxt = cxt;
  }

  /**
   * get list of adrotation positions
   * @private
   */
  _getAdRotationList(): Array<string> {
    //get only the ads for the current page;
    return u.intersect(adconfig.getPagePositions(null, null, true), this._config.positions);
  }

  /**
   * returns whether to rotate or not
   * @private
   */
  _shouldRotate(): boolean {
    var bPassed = ((this._pageStart > 0) &&
      (this._config.durationEnd > 0) &&
      (new Date().getTime() - this._pageStart > (this._config.durationEnd * 1000)));
    //we should get ad rotation timer value
    //current page shouldnt be in exclude list
    //current page has adrotation positions
    return !bPassed &&
      (this._config.delay > 0 ) &&
      !u.isBrowserExcluded(adconfig.getPageName(), this._config.excludeList) &&
      this._getAdRotationList().length > 0;
  }

  /**
   * takes care of ad rotation
   * @private
   */
  _rotate() {
    this._tidRotate = 0;
    let list = this._getAdRotationList();
    if (list.length && this._getAdsCB !== null && u.isFunction(this._getAdsCB)) {
      u.logDebug(`Rotating ads ${list.join(',')}`);
      this._getAdsCB.call(this._getAdsCBCxt, adconfig.getPageName(), list, false, true);
    }
  }

  /**
   * trigger ad rotation
   */
  trigger() {
    if (!this._shouldRotate()) {
      return;
    }

    //Resetting adRotation timer to handle the below case
    //1. Loaded subset of positions and delayList became empty.
    //2. Set the adRotation timer.
    //3. Just completed loading remaining subset of positions and now delayList is empty again.
    //4. Now clear previous adRotation timer and set it.
    this.clear();

    //we have certain position to be fetched. Let's start timer here.
    var that = this;
    this._tidRotate = setTimeout(() => that._rotate(), this._config.delay * 1000);
  }

  /**
   * clear ad rotation
   */
  clear() {
    if (this._tidRotate) {
      clearTimeout(this._tidRotate);
      this._tidRotate = 0;
    }
  }

  /**
   * getter for ad rotation positions
   * @return {Array<string>}
   */
  getPositions(): Array<string> {
    return this._config.positions;
  }
}

//export singleton object as default for this module
export default new _adrotation();
